/**
 * Regions Selectors
 * Memoized selectors for accessing regions state
 */

import { createSelector } from '@ufc/core'
import type { RegionsState } from './regions.slice'
import type { RegionListItem, RegionsFilter } from '../domain/types'

// ===========================================
// Root State Type
// ===========================================

interface RootState {
  regions: RegionsState
}

// ===========================================
// Base Selector
// ===========================================

const selectRegionsState = (state: RootState) => state.regions

// ===========================================
// Simple Selectors
// ===========================================

export const selectRegions = createSelector(
  selectRegionsState,
  (state) => state.items
)

export const selectSelectedRegion = createSelector(
  selectRegionsState,
  (state) => state.selectedRegion
)

export const selectRegionStats = createSelector(
  selectRegionsState,
  (state) => state.stats
)

export const selectRegionsFilter = createSelector(
  selectRegionsState,
  (state) => state.filter
)

export const selectRegionsPagination = createSelector(
  selectRegionsState,
  (state) => state.pagination
)

export const selectAvailableFactories = createSelector(
  selectRegionsState,
  (state) => state.availableFactories
)

export const selectAvailableCountries = createSelector(
  selectRegionsState,
  (state) => state.availableCountries
)

export const selectRegionsLoading = createSelector(
  selectRegionsState,
  (state) => state.loading
)

export const selectRegionDetailLoading = createSelector(
  selectRegionsState,
  (state) => state.detailLoading
)

export const selectRegionsError = createSelector(
  selectRegionsState,
  (state) => state.error
)

// ===========================================
// Filter Utility (external function)
// ===========================================

function filterRegions(items: RegionListItem[], filter: RegionsFilter): RegionListItem[] {
  let result = [...items]

  // Filter by active status
  if (filter.isActive !== undefined && filter.isActive !== 'all') {
    result = result.filter((item) => item.isActive === filter.isActive)
  }

  // Filter by search
  if (filter.search) {
    const searchLower = filter.search.toLowerCase()
    result = result.filter(
      (item) =>
        item.name.toLowerCase().includes(searchLower) ||
        item.code.toLowerCase().includes(searchLower)
    )
  }

  // Filter by country
  if (filter.countryId) {
    result = result.filter((item) => item.countryId === filter.countryId)
  }

  return result
}

// ===========================================
// Filtered Selector
// ===========================================

export const selectFilteredRegions = createSelector(
  [selectRegions, selectRegionsFilter],
  (items, filter) => filterRegions(items, filter)
)

// ===========================================
// Computed Selectors
// ===========================================

export const selectRegionsCount = createSelector(selectRegions, (items) => items.length)

export const selectRegionByDocumentId = (documentId: string) =>
  createSelector(selectRegions, (items) =>
    items.find((item) => item.documentId === documentId) ?? null
  )

export const selectActiveRegions = createSelector(selectRegions, (items) =>
  items.filter((item) => item.isActive)
)

// ===========================================
// UI State Selectors
// ===========================================

export const selectIsRegionsEmpty = createSelector(
  [selectRegions, selectRegionsLoading],
  (items, loading) => !loading && items.length === 0
)

export const selectHasRegionsError = createSelector(
  selectRegionsError,
  (error) => error !== null
)

// ===========================================
// Summary Selectors (for dropdowns)
// ===========================================

export const selectRegionSummaries = createSelector(selectActiveRegions, (regions) =>
  regions.map((r) => ({
    id: r.id,
    documentId: r.documentId,
    code: r.code,
    name: r.name,
    isActive: r.isActive,
  }))
)
